import type { SimEvent } from '@fafi/shared';

// D18 presentation extras (§13.3/§13.4): ref-call banners + procedural WebAudio SFX + a 2D radar.
// ui/ consumes ViewState-derived data + SimEvents only (T-ARCH-004). Zero assets: the whistle, thump
// and chord are synthesized. Team naming: 0 = Blue, 1 = Red (kit colors, pre-M7 club names).

const TEAM_NAME = ['Blue', 'Red'] as const;
const RESTART_LABEL: Record<string, string> = {
  THROW_IN: 'Throw-in',
  KICK_IN: 'Kick-in',
  CORNER: 'Corner',
  GOAL_KICK: 'Goal kick',
  FREE_KICK: 'Free kick',
  PENALTY: 'PENALTY',
  DROP_BALL: 'Drop ball',
  KICKOFF: 'Kick-off',
};

/** Banner text for an event, or null when it isn't banner-worthy (D9e-1: every ref call, with team).
 *  D83: `nameOf` resolves an entity to its label — a session display name for a human, "#7" (jersey back
 *  number) for a bot — so a foul banner says WHO fouled WHOM ("FREE KICK — #7 fouled #10"). */
export function bannerFor(e: SimEvent, nameOf: (id: number) => string = (id) => `#${id}`, ownId: number | null = null): string | null {
  switch (e.t) {
    case 'GOAL':
      return `⚽ GOAL — ${TEAM_NAME[e.team]}${e.ownGoal ? ' (own goal)' : ''} · goalie restart next`;
    case 'OUT_OF_PLAY': {
      // D123: the restart names its taker on the wire now, so the banner can tell you it is YOURS —
      // previously a free kick was awarded and nothing on screen said who was supposed to take it.
      const label = `${(RESTART_LABEL[e.restart] ?? e.restart).toUpperCase()} — ${TEAM_NAME[e.team]}`;
      return ownId !== null && e.taker === ownId ? `${label} · YOU take it` : label;
    }
    case 'FOUL':
      return `${(RESTART_LABEL[e.restart] ?? 'Free kick').toUpperCase()} — ${nameOf(e.offender)} fouled ${nameOf(e.victim)}`;
    case 'CARD':
      return e.card === 'RED' ? '🟥 RED CARD — off for 20s' : '🟨 Yellow card';
    case 'RETURN':
      return '↩ Back on!'; // D65c: the 20 s suspension elapsed
    case 'SAVE':
      return e.held ? 'Caught by the keeper!' : 'Parried!';
    case 'GK_PICKUP':
      return 'Keeper picks it up';
    case 'GK_SMOTHER':
      return 'Smothered by the keeper!';
    case 'WHISTLE':
      return e.kind === 'HALF_END' ? 'Half time' : e.kind === 'FULL_END' ? 'FULL TIME' : e.kind === 'KICKOFF' ? 'Kick-off' : null;
    default:
      return null;
  }
}

/** Fixed-position banner element; shows ~2.2 s per call, newest wins. */
export class BannerOverlay {
  private readonly el: HTMLDivElement;
  private hideAt = 0;
  constructor(doc: Document = document) {
    this.el = doc.createElement('div');
    this.el.style.cssText =
      'position:fixed;top:16%;left:50%;transform:translateX(-50%);padding:10px 26px;border-radius:10px;' +
      'background:rgba(5,20,12,.82);color:#eaffea;font:600 22px system-ui;letter-spacing:.5px;' +
      'pointer-events:none;opacity:0;transition:opacity .18s;z-index:60';
    doc.body.appendChild(this.el);
  }
  show(text: string, nowMs: number, durationMs = 2200): void {
    this.el.textContent = text;
    this.el.style.opacity = '1';
    this.hideAt = nowMs + durationMs; // D83: a restart banner rides the ceremony window (read from cfg)
  }
  frame(nowMs: number): void {
    if (this.hideAt > 0 && nowMs >= this.hideAt) {
      this.el.style.opacity = '0';
      this.hideAt = 0;
    }
  }
}

/** The slice of AudioContext this synth needs — structural, so ui/ never names the device global (T-ARCH-004). */
export interface AudioCtxLike {
  currentTime: number;
  state: string;
  destination: AudioNode;
  resume(): Promise<void>;
  createOscillator(): OscillatorNode;
  createGain(): GainNode;
}

/** Procedural SFX (D9e-2): whistle / goal chord / kick tick / save thump — no audio assets. */
export class Sfx {
  private ctx: AudioCtxLike | null = null;
  /** The AudioContext is INJECTED (T-ARCH-004 — ui/ never touches device globals directly). */
  constructor(private readonly createCtx: () => AudioCtxLike | null) {}
  private ensure(): AudioCtxLike | null {
    if (!this.ctx) {
      try {
        this.ctx = this.createCtx();
      } catch {
        return null;
      }
    }
    if (!this.ctx) return null;
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }
  private tone(freq: number, dur: number, type: OscillatorType, gain: number, delay = 0): void {
    const ctx = this.ensure();
    if (!ctx) return;
    const t0 = ctx.currentTime + delay;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.setValueAtTime(gain, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g).connect(ctx.destination);
    o.start(t0);
    o.stop(t0 + dur);
  }
  whistle(long = false): void {
    this.tone(2200, long ? 0.7 : 0.28, 'square', 0.06);
    this.tone(2350, long ? 0.7 : 0.28, 'square', 0.04);
  }
  goal(): void {
    [523, 659, 784].forEach((f, i) => this.tone(f, 0.5, 'triangle', 0.12, i * 0.09));
  }
  thump(): void {
    this.tone(140, 0.12, 'sine', 0.25);
  }
  /** D44: a soft low tick for a sprint knock-on touch (much quieter than a save thump). */
  softTouch(): void {
    this.tone(190, 0.07, 'sine', 0.07);
  }
  /** D82: an airy "whoosh" for a WHIFFED tackle — a swing through empty air (failed-tackle feedback). */
  whoosh(): void {
    this.tone(520, 0.13, 'sawtooth', 0.045);
    this.tone(300, 0.15, 'sawtooth', 0.035, 0.03);
  }
  forEvent(e: SimEvent): void {
    // one whistle per stoppage: the canonical WHISTLE event covers fouls/goals/half+full-time/kickoff
    // (FOUL/CARD co-emit a WHISTLE, so they must NOT whistle again here — D44: no double whistle).
    if (e.t === 'GOAL') this.goal();
    else if (e.t === 'WHISTLE') this.whistle(e.kind === 'FULL_END' || e.kind === 'HALF_END');
    else if (e.t === 'OUT_OF_PLAY') this.whistle(); // D44: throw/kick-in/corner/goal-kick now whistle too
    else if (e.t === 'SAVE' || e.t === 'GK_SMOTHER' || e.t === 'GK_PICKUP') this.thump();
    else if (e.t === 'SHOT') this.thump();
  }
}

/** 2D radar strip (D9b): dots for both teams (GK neon), the ball, and the controlled player. */
export class Radar {
  private readonly cv: HTMLCanvasElement;
  private readonly g: CanvasRenderingContext2D | null;
  constructor(
    private readonly pitchLength: number,
    private readonly pitchWidth: number,
    doc: Document = document,
  ) {
    this.cv = doc.createElement('canvas');
    this.cv.width = 240;
    this.cv.height = Math.max(90, Math.round((240 * pitchWidth) / pitchLength));
    this.cv.style.cssText =
      'display:none;position:fixed;bottom:14px;left:50%;transform:translateX(-50%);opacity:.85;border:1px solid rgba(255,255,255,.35);' +
      'border-radius:6px;background:rgba(10,40,22,.75);pointer-events:none;z-index:50';
    doc.body.appendChild(this.cv);
    this.g = this.cv.getContext('2d');
  }
  draw(view: {
    entities: { pos: { x: number; y: number }; team: number; isControlled: boolean; isGk: boolean }[];
    ball: { pos: { x: number; y: number } };
  }): void {
    const g = this.g;
    if (!g) return;
    const W = this.cv.width;
    const H = this.cv.height;
    g.clearRect(0, 0, W, H);
    g.strokeStyle = 'rgba(255,255,255,.4)';
    g.beginPath();
    g.moveTo(W / 2, 0);
    g.lineTo(W / 2, H);
    g.stroke();
    const px = (x: number): number => ((x + this.pitchLength / 2) / this.pitchLength) * W;
    const py = (y: number): number => ((y + this.pitchWidth / 2) / this.pitchWidth) * H;
    for (const e of view.entities) {
      g.fillStyle = e.isGk ? (e.team === 0 ? '#00e5ff' : '#ff7b00') : e.team === 0 ? '#3a86ff' : '#ef476f';
      const r = e.isControlled ? 4 : 2.6;
      g.beginPath();
      g.arc(px(e.pos.x), py(e.pos.y), r, 0, Math.PI * 2);
      g.fill();
      if (e.isControlled) {
        g.strokeStyle = '#fff';
        g.stroke();
      }
    }
    g.fillStyle = '#fff';
    g.beginPath();
    g.arc(px(view.ball.pos.x), py(view.ball.pos.y), 2.2, 0, Math.PI * 2);
    g.fill();
  }
}

// ─────────────────────────── D123 — the set-piece ceremony cue ───────────────────────────

/**
 * D123 — how long until the whistle, derived client-side.
 *
 * D122 made the ceremony short and put `WHISTLE{RESTART}` on the wire, but nothing on screen said the
 * ball was ABOUT to be playable — you either guessed or pressed and got nothing. The countdown needs no
 * new wire field: `OUT_OF_PLAY` carries the restart kind, and the ceremony length for that kind is in
 * the config the client already receives (T-NET-016). Pure, so it is testable without a DOM.
 *
 * Returns whole seconds remaining (3·2·1), or null once the whistle has gone / no restart is pending.
 */
export function ceremonySecondsLeft(ceremonyTicks: number, elapsedMs: number): number | null {
  const totalMs = (ceremonyTicks / 30) * 1000;
  const leftMs = totalMs - elapsedMs;
  if (leftMs <= 0) return null;
  return Math.max(1, Math.ceil(leftMs / 1000));
}

/**
 * The on-screen half: a countdown while a restart's ceremony runs, and a subdued "wait for the whistle"
 * cue when the player presses a kick during it (previously a whiffed press gave zero feedback, which is
 * indistinguishable from a broken button). textContent-only (T-SEC-006).
 */
export class CeremonyCue {
  private readonly el: HTMLDivElement;
  private ceremonyTicks = 0;
  private startedMs = -1;
  private nagUntilMs = 0;

  constructor(doc: Document) {
    this.el = doc.createElement('div');
    this.el.style.cssText =
      'position:fixed;left:50%;top:22%;transform:translateX(-50%);padding:5px 14px;border-radius:14px;' +
      'background:rgba(6,14,10,.72);color:#cfe9d4;font:700 15px ui-monospace,monospace;z-index:190;' +
      'pointer-events:none;opacity:0;transition:opacity .12s';
    doc.body.appendChild(this.el);
  }

  /** A restart was awarded: start counting its ceremony down. */
  begin(ceremonyTicks: number, nowMs: number): void {
    this.ceremonyTicks = ceremonyTicks;
    this.startedMs = nowMs;
    this.nagUntilMs = 0;
  }

  /** The whistle went (WHISTLE{RESTART}) — stop counting. */
  end(): void {
    this.startedMs = -1;
  }

  /** The player pressed a kick while the ball was still dead. */
  nudge(nowMs: number): void {
    if (this.startedMs < 0) return; // only meaningful during a ceremony
    this.nagUntilMs = nowMs + 900;
  }

  frame(nowMs: number): void {
    const left = this.startedMs < 0 ? null : ceremonySecondsLeft(this.ceremonyTicks, nowMs - this.startedMs);
    if (left === null) {
      this.startedMs = -1;
      this.el.style.opacity = '0';
      return;
    }
    this.el.textContent = nowMs < this.nagUntilMs ? 'wait for the whistle…' : `whistle in ${left}`;
    this.el.style.opacity = nowMs < this.nagUntilMs ? '1' : '.82';
  }

  /** Test seam. */
  get text(): string {
    return this.el.textContent ?? '';
  }
  get visible(): boolean {
    return this.el.style.opacity !== '0';
  }
}
